import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Activity,
  CheckCircle,
  XCircle,
  RefreshCw,
  Zap,
  Database,
  Server,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  BarChart3,
} from 'lucide-react';
import { Card, Loading, Badge } from '../../components/common';
import adminService from '../../services/api/admin.service';

// 🔥 Função para detectar status real baseado no context
const getConversationStatus = (conversation) => {
  let currentStep = '';
  
  if (conversation.context) {
    currentStep = conversation.context.current_step || 
                 conversation.context.currentStep || 
                 conversation.context.step ||
                 conversation.context.stepId ||
                 conversation.context.step_id || '';
  }
  
  if (!currentStep && conversation) {
    currentStep = conversation.current_step ||
                 conversation.currentStep ||
                 conversation.step || '';
  }
  
  const stepStr = String(currentStep).toLowerCase();
  
  if (stepStr.includes('perguntar_reinicio')) {
    return 'closed';
  }
  
  if (conversation.context?.flow_finished === true || 
      conversation.context?.flow_finished === 'true') {
    return 'closed';
  }
  
  if (conversation.context?.restarted_by_user === true || 
      conversation.context?.restarted_by_user === 'true') {
    return 'open';
  }
  
  return conversation.status || 'open';
};

const AdminOverview = () => {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const healthData = await adminService.getHealth();
      setHealth(healthData);

      const metricsData = await adminService.calculateMetrics();
      setMetrics(metricsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutDashboard className="w-7 h-7 text-primary-500" />
            Dashboard Administrativo
          </h1>
          <p className="text-gray-600 mt-1">
            Monitoramento em tempo real da plataforma
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Atualizar</span>
        </button>
      </div>

      {health && (
        <Card>
          <Card.Body>
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className={`p-3 rounded-lg ${
                    health.status === 'ok' ? 'bg-green-100' : 'bg-yellow-100'
                  }`}
                >
                  <Activity
                    className={`w-6 h-6 ${
                      health.status === 'ok' ? 'text-green-600' : 'text-yellow-600'
                    }`}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Status do Sistema</h3>
                  <p className="text-sm text-gray-600">
                    {health.status === 'ok'
                      ? 'Todos os sistemas operacionais'
                      : 'Alguns serviços degradados'}
                  </p>
                </div>
              </div>
              <Badge variant={health.status === 'ok' ? 'success' : 'warning'} dot>
                {health.status === 'ok' ? 'Operacional' : 'Degradado'}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <ServiceStatus
                icon={Server}
                name="API"
                description="Aplicação principal"
                status={health.services.api}
              />
              <ServiceStatus
                icon={Database}
                name="Database"
                description="PostgreSQL"
                status={health.services.database}
              />
              <ServiceStatus
                icon={Activity}
                name="Redis"
                description="Cache & Queue"
                status={health.services.redis}
              />
              <ServiceStatus
                icon={MessageSquare}
                name="Evolution"
                description="WhatsApp Provider"
                status={health.services.evolution}
              />
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Uptime: {Math.floor(health.uptime / 60)} minutos</span>
              <span className="mx-2">•</span>
              <span>
                Última verificação: {new Date(health.timestamp).toLocaleTimeString('pt-BR')}
              </span>
            </div>
          </Card.Body>
        </Card>
      )}

      {metrics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              icon={Users}
              iconColor="blue"
              title="Total de Tenants"
              value={metrics.totalTenants}
              subtitle={`${metrics.activeTenants} ativos, ${metrics.inactiveTenants} inativos`}
              trend={
                metrics.activeTenants > metrics.inactiveTenants
                  ? { type: 'up', value: 'Maioria ativa' }
                  : null
              }
            />

            <MetricCard
              icon={MessageSquare}
              iconColor="purple"
              title="Conversas Totais"
              value={metrics.totalConversations}
              subtitle={`${metrics.openConversations} abertas, ${metrics.closedConversations} fechadas`}
            />

            <MetricCard
              icon={Zap}
              iconColor="green"
              title="Bots Ativos"
              value={metrics.botsActive}
              subtitle={`${metrics.botsPaused} pausados`}
              trend={
                metrics.botsActive > metrics.botsPaused
                  ? { type: 'up', value: 'Maioria ativa' }
                  : { type: 'down', value: 'Atenção necessária' }
              }
            />

            <MetricCard
              icon={TrendingUp}
              iconColor="yellow"
              title="Taxa de Resolução"
              value={`${metrics.resolutionRate}%`}
              subtitle={`${metrics.closedConversations} resolvidas`}
              trend={
                parseFloat(metrics.resolutionRate) > 70
                  ? { type: 'up', value: 'Excelente' }
                  : { type: 'down', value: 'Pode melhorar' }
              }
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <Card.Body>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Envio de Mensagens</h3>
                  <MessageSquare className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Habilitado</span>
                    <span className="text-lg font-semibold text-green-600">
                      {metrics.sendEnabled}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Bloqueado</span>
                    <span className="text-lg font-semibold text-red-600">
                      {metrics.sendDisabled}
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Coexistência</h3>
                  <Users className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Com agentes</span>
                    <span className="text-lg font-semibold text-primary-600">
                      {metrics.coexEnabled}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Bot pausado</span>
                    <span className="text-lg font-semibold text-yellow-600">
                      {metrics.botPausedCount}
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Status Geral</h3>
                  <Activity className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Operacionais</span>
                    <span className="text-lg font-semibold text-green-600">
                      {metrics.botsActive}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Necessitam atenção</span>
                    <span className="text-lg font-semibold text-yellow-600">
                      {metrics.botsPaused + metrics.sendDisabled}
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <div>
                  <Card.Title>Conversas por Tenant</Card.Title>
                  <Card.Description>Distribuição de conversas entre os tenants</Card.Description>
                </div>
                <BarChart3 className="w-5 h-5 text-gray-400" />
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                {metrics.conversationsByTenant.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum tenant com conversas
                  </div>
                ) : (
                  metrics.conversationsByTenant
                    .sort((a, b) => b.total - a.total)
                    .map((item) => (
                      <div
                        key={item.tenantId}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {item.tenantName}
                          </h3>
                          <p className="text-sm text-gray-500 truncate">{item.tenantId}</p>
                        </div>

                        <div className="flex items-center gap-6 ml-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-gray-900">{item.total}</p>
                            <p className="text-xs text-gray-500">Total</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-semibold text-green-600">{item.open}</p>
                            <p className="text-xs text-gray-500">Abertas</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-semibold text-gray-600">{item.closed}</p>
                            <p className="text-xs text-gray-500">Fechadas</p>
                          </div>
                          {item.botPaused > 0 && (
                            <div className="text-center">
                              <p className="text-lg font-semibold text-yellow-600">
                                {item.botPaused}
                              </p>
                              <p className="text-xs text-gray-500">Pausadas</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </Card.Body>
          </Card>

          {(metrics.sendDisabled > 0 || metrics.botsPaused > 0 || metrics.botPausedCount > 0) && (
            <Card>
              <Card.Body>
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">Atenção Necessária</h3>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {metrics.sendDisabled > 0 && (
                        <li>• {metrics.sendDisabled} tenant(s) com envio bloqueado</li>
                      )}
                      {metrics.botsPaused > 0 && (
                        <li>• {metrics.botsPaused} tenant(s) com bot pausado</li>
                      )}
                      {metrics.botPausedCount > 0 && (
                        <li>• {metrics.botPausedCount} conversa(s) com bot pausado (handoff)</li>
                      )}
                    </ul>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

const ServiceStatus = ({ icon: Icon, name, description, status }) => (
  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
    <Icon className="w-5 h-5 text-gray-600" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
      <p className="text-xs text-gray-500 truncate">{description}</p>
    </div>
    {status === 'healthy' ? (
      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
    ) : status === 'not_configured' ? (
      <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
    )}
  </div>
);

const MetricCard = ({ icon: Icon, iconColor, title, value, subtitle, trend }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
  };

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
            <p className="text-xs text-gray-500">{subtitle}</p>
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                {trend.type === 'up' ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )}
                <span
                  className={`text-xs font-medium ${
                    trend.type === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {trend.value}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[iconColor]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AdminOverview;